import {LabelToken} from '@github-ui/label-token'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {Link} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/styled-react'
import {Box, Tooltip} from '@primer/styled-react'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useMemo, type ReactNode} from 'react'

import styles from './LabelsList.module.css'
import {isFeatureEnabled} from '@github-ui/feature-flags'

/**
 * This interface is the same as that of @github-ui/item-picker/label's Label
 * but it doesn't seem appropriate to couple this list to the picker
 * in that way
 */
export interface Label {
  readonly id: string
  readonly url: string
  readonly name: string
  readonly nameHTML: string
  readonly color: string
  readonly description: string | null | undefined
}

export type LabelsListProps = {
  labels: Label[]
  testId?: string
  sx?: BetterSystemStyleObject
  /** We display this string when the list of labels is empty, defaults to 'No labels' */
  emptyText?: ReactNode
  /** Optional callback to run if a label is removed */
  onDelete?: (id: string) => void
}

/**
 *
 * A list of label tokens sorted by name.
 * This component is used in the Issues and Pull Requests sidebars.
 * If we find either one needs to diverge too much we should consider
 * making two separate components instead of trying to make this one fit both.
 *
 */
export function LabelsList({labels, testId, sx, onDelete, emptyText = 'No labels'}: LabelsListProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const report_sidebar_interactions = isFeatureEnabled('issues_report_sidebar_interactions')
  const repoIssuesPath = useMemo(() => {
    if (labels.length === 0 || !labels[0]) return ''
    return `${labels[0].url.slice(0, labels[0].url.lastIndexOf('/labels/'))}/issues`
  }, [labels])

  const labelIndexUrl = useCallback(
    (label: Label) => `${repoIssuesPath}?q=${encodeURIComponent(`state:open label:"${label.name}"`)}`,
    [repoIssuesPath],
  )

  return (
    <Box sx={sx} tabIndex={-1} data-testid={testId} className={styles.labelsListContainer}>
      {labels.length > 0
        ? labels
            .sort((a, b) => (a.name === b.name ? 0 : a.name > b.name ? 1 : -1))
            .map(label => (
              <Tooltip
                text={label.description ?? ''}
                type="description"
                aria-label={label.description ?? ''}
                sx={{
                  visibility: label.description ? 'visible' : 'hidden',
                }}
                key={label.id}
                className={styles.labelDescriptionTooltip}
              >
                <Link
                  href={labelIndexUrl(label)}
                  key={label.id}
                  muted
                  aria-describedby={`${label.id}-tooltip`}
                  className={styles.labelLink}
                  onClick={() => {
                    if (report_sidebar_interactions) {
                      sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_LABEL_CLICK')
                    }
                  }}
                  onContextMenu={() => {
                    if (report_sidebar_interactions) {
                      sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_LABEL_RIGHT_CLICK')
                    }
                  }}
                >
                  <LabelToken
                    text={<VerifiedHTMLText html={label.nameHTML as SafeHTMLString} />}
                    onDelete={onDelete ? () => onDelete(label.id) : undefined}
                    fillColor={`#${label.color}`}
                    key={label.id}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      maxWidth: '100%',
                    }}
                  />
                  <span className="sr-only" id={`${label.id}-tooltip`}>
                    {label.description ?? ''}
                  </span>
                </Link>
              </Tooltip>
            ))
        : emptyText}
    </Box>
  )
}

try{ LabelsList.displayName ||= 'LabelsList' } catch {}